import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { fmtEUR } from "../lib/format";
import { DETTAGLI, FAMIGLIE } from "../lib/constants";

export default function DatiMancantiPage() {
  const { storeId, isMaster } = useOutletContext();
  const [data, setData] = useState(null);
  const [map, setMap] = useState({});
  const [regole, setRegole] = useState(null);
  const [nuovaRegola, setNuovaRegola] = useState({ tipo: "nome", valore: "" });
  const [analisi, setAnalisi] = useState(null);
  const [analisiBusy, setAnalisiBusy] = useState(false);
  const [showCatalogo, setShowCatalogo] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const loadRegole = () => api.get("/impostazioni/righe-non-merce").then((r) => setRegole(r.data)).catch(() => {});
  useEffect(() => { loadRegole(); }, []);
  useEffect(() => { api.get("/analisi/sku-mancanti").then((r) => setAnalisi(r.data)).catch(() => {}); }, []);

  const generaAnalisi = async () => {
    setAnalisiBusy(true);
    try {
      const r = await api.post("/analisi/sku-mancanti");
      setAnalisi(r.data);
      toast.success("Analisi completata: file pronti da scaricare");
    } catch (e) { toast.error(formatApiError(e)); }
    setAnalisiBusy(false);
  };

  const salvaRegole = async (lista) => {
    try {
      await api.put("/impostazioni/righe-non-merce", { globali: lista });
      loadRegole();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const aggiungiRegola = async (valore, tipo) => {
    const v = (valore ?? nuovaRegola.valore).trim();
    if (!v) return toast.error("Inserisci un valore per la regola");
    await salvaRegole([...(regole?.globali || []), { tipo: tipo || nuovaRegola.tipo, valore: v }]);
    setNuovaRegola({ tipo: "nome", valore: "" });
  };

  const load = () => {
    api.get(`/dati-mancanti?store_id=${storeId}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { setData(null); load(); }, [storeId]);

  const salvaMapping = async (raw) => {
    const m = map[raw];
    if (!m?.famiglia || !m?.dettaglio) return toast.error("Seleziona famiglia e dettaglio");
    try {
      const res = await api.post("/gateway-mappings", { raw, famiglia: m.famiglia, dettaglio: m.dettaglio });
      toast.success(`Mappatura salvata, ${res.data.ordini_aggiornati} ordini aggiornati`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="text-sm text-slate-500">Caricamento…</div>;
  const righeSenzaSku = data.righe_senza_sku || [];
  const prodCostoVuoto = data.prod_costo_vuoto_n ?? new Set((data.righe_senza_costo || []).map((r) => r.sku)).size;
  const skuNonMaster = data.sku_non_trovati_master || [];
  const tot = data.metodi_da_mappare.length + data.righe_senza_costo.length
    + righeSenzaSku.length + skuNonMaster.length + (data.costo_incompatibili || []).length;

  return (
    <div data-testid="dati-mancanti-page" className="space-y-6">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">
        Dati mancanti <span data-testid="missing-count" className="text-base font-mono text-amber-600">({tot})</span>
      </h1>

      <div data-testid="blocco-impatto">
        <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900 mb-1">Problemi che stanno falsando i tuoi numeri</h2>
        <p className="text-xs text-slate-400 mb-3">Riguardano ordini realmente esistenti: finché non li correggi, costi e margini del periodo restano sbagliati. Ordinati per impatto — in cima ciò che conviene sistemare per primo.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h2 className="text-sm font-semibold text-slate-800">Righe ordine senza costo ({data.righe_senza_costo.length} righe · {prodCostoVuoto} prodotti con costo vuoto su Shopify in tutto il catalogo)</h2>
          <a data-testid="export-righe-senza-costo" href={`${API}/api/dati-mancanti/export/righe_senza_costo?store_id=${storeId}`} target="_blank" rel="noreferrer"
            className="text-xs font-semibold text-indigo-600 border border-indigo-200 rounded px-3 py-1.5 hover:bg-indigo-50">Scarica elenco di lavoro (CSV)</a>
        </div>
        <p className="text-xs text-slate-400 mb-2">Come si risolve: compila il campo "Costo per articolo" su Shopify per questi prodotti — la prossima sincronizzazione aggiornerà i valori. Il CSV contiene identificativo variante, prezzo di vendita, costo reale master e cessione suggerita, così sai già che valore inserire.</p>
        {(data.righe_senza_costo_prodotti || []).length === 0 ? <p className="text-sm text-slate-400">Nessuna riga senza costo.</p> : (
          <table className="w-full text-sm" data-testid="righe-mancanti-table">
            <thead><tr className="text-left border-b border-slate-200">
              <th className="py-1.5 text-xs font-bold uppercase text-slate-600">Prodotto</th>
              <th className="text-xs font-bold uppercase text-slate-600 text-right">Varianti</th>
              <th className="text-xs font-bold uppercase text-slate-600 text-right">Righe ordine</th>
              <th className="text-xs font-bold uppercase text-slate-600 text-right">Valore venduto</th>
            </tr></thead>
            <tbody>
              {data.righe_senza_costo_prodotti.slice(0, 100).map((g) => (
                <tr key={g.prodotto} className="border-b border-slate-50">
                  <td className="py-1.5 text-xs">{g.prodotto}</td>
                  <td className="font-mono text-xs text-right">{g.varianti}</td>
                  <td className="font-mono text-xs text-right font-semibold text-amber-700">{g.righe}</td>
                  <td className="font-mono text-xs text-right tnum">{fmtEUR(g.valore_venduto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="text-xs text-slate-400 mt-2">Raggruppato per prodotto e ordinato per impatto: nel CSV ci sono TUTTE le varianti, che sono quelle da correggere su Shopify.</p>
        <p className="text-xs text-slate-400 mt-2">Il costo mancante conta come 0 finché non viene compilato su Shopify.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h2 className="text-sm font-semibold text-slate-800">Righe ordine senza SKU ({righeSenzaSku.length})</h2>
          <a data-testid="export-righe-senza-sku" href={`${API}/api/dati-mancanti/export/righe_senza_sku?store_id=${storeId}`} target="_blank" rel="noreferrer"
            className="text-xs font-semibold text-indigo-600 border border-indigo-200 rounded px-3 py-1.5 hover:bg-indigo-50">Scarica CSV</a>
        </div>
        <p className="text-xs text-slate-400 mb-2">
          Su Shopify queste righe non hanno SKU, quindi il costo merce non è agganciabile: nella maggior parte dei casi sono righe di servizio
          (es. "Commissione Contrassegno" da 3 €), non merce. Come si risolve: se una riga è un prodotto vero, assegna lo SKU alla variante su Shopify
          (l'analisi sotto propone gli SKU AUTOGEN-) e la prossima sincronizzazione aggancia il costo.
        </p>
        {righeSenzaSku.length === 0 ? <p className="text-sm text-slate-400">Nessuna riga senza SKU.</p> : (
          <table className="w-full text-sm" data-testid="righe-senza-sku-table">
            <thead><tr className="text-left border-b border-slate-200">
              <th className="py-1.5 text-xs font-bold uppercase text-slate-600">Ordine</th>
              <th className="text-xs font-bold uppercase text-slate-600">Riga</th>
            </tr></thead>
            <tbody>
              {righeSenzaSku.slice(0, 100).map((r, i) => (
                <tr key={i} className="border-b border-slate-50">
                  <td className="py-1.5 font-mono text-xs">{r.id_ordine_esterno}</td>
                  <td className="text-xs text-slate-500">{r.nome || <span className="text-amber-600">senza SKU</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h2 className="text-sm font-semibold text-slate-800">SKU non trovati sul master ({skuNonMaster.length})</h2>
          <a data-testid="export-sku-non-master" href={`${API}/api/dati-mancanti/export/sku_non_trovati_master?store_id=${storeId}`} target="_blank" rel="noreferrer"
            className="text-xs font-semibold text-indigo-600 border border-indigo-200 rounded px-3 py-1.5 hover:bg-indigo-50">Scarica CSV</a>
        </div>
        <p className="text-xs text-slate-400 mb-2">
          SKU presenti su uno store figlio ma assenti dal catalogo dello store master: per questi il costo reale fornitore e il ricarico non sono calcolabili.
          Come si risolve: crea il prodotto sul catalogo master con lo stesso SKU — dalla prossima sincronizzazione costo reale e ricarico diventano calcolabili.
          {data.master_stato === "senza_prodotti" && " Attenzione: lo store master non ha ancora prodotti importati."}
          {data.master_stato === "non_configurato" && " Attenzione: nessuno store master configurato."}
        </p>
        {skuNonMaster.length === 0 ? <p className="text-sm text-slate-400">Tutti gli SKU dei figli sono presenti sul master.</p> : (
          <table className="w-full text-sm" data-testid="sku-non-master-table">
            <thead><tr className="text-left border-b border-slate-200">
              <th className="py-1.5 text-xs font-bold uppercase text-slate-600">SKU</th>
              <th className="text-xs font-bold uppercase text-slate-600">Prodotto</th>
              <th className="text-xs font-bold uppercase text-slate-600">Store</th>
            </tr></thead>
            <tbody>
              {skuNonMaster.slice(0, 100).map((p, i) => (
                <tr key={i} className="border-b border-slate-50">
                  <td className="py-1.5 font-mono text-xs">{p.sku}</td>
                  <td className="text-xs">{p.nome}</td>
                  <td className="text-xs text-slate-500">{p.store_nome}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="sku-analisi-block">
        <h2 className="text-sm font-semibold text-slate-800 mb-1">Assegnazione SKU mancanti — analisi figlio ↔ master</h2>
        <p className="text-xs text-slate-400 mb-3">
          Lettura dei cataloghi Shopify di Yamari e del master (sola lettura, nessuna scrittura): confronto per nome normalizzato a livello di variante.
          Produce tre file: coppie certe (con SKU proposto, prefisso AUTOGEN-), ambigui e senza corrispondenza.
        </p>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button data-testid="sku-analisi-run" onClick={generaAnalisi} disabled={analisiBusy}
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded px-3 py-1.5 disabled:opacity-40">
            {analisiBusy ? "Analisi in corso…" : "Genera / aggiorna analisi"}
          </button>
          {analisi && ["certe", "ambigue", "senza_corrispondenza"].map((g) => (
            <a key={g} data-testid={`sku-analisi-dl-${g}`} href={`${API}/api/analisi/sku-mancanti/export/${g}`} target="_blank" rel="noreferrer"
              className="text-xs font-semibold text-indigo-600 border border-indigo-200 rounded px-3 py-1.5 hover:bg-indigo-50">
              Scarica {g.replace("_", " ")} (CSV)
            </a>
          ))}
        </div>
        {analisi && (
          <div data-testid="sku-analisi-summary" className="text-xs text-slate-600 space-y-0.5">
            <p>Senza SKU: <b className="font-mono">{analisi.senza_sku_yamari}</b> prodotti Yamari ({analisi.varianti_senza_sku_yamari} varianti) · <b className="font-mono">{analisi.senza_sku_master}</b> prodotti master ({analisi.varianti_senza_sku_master} varianti)</p>
            <p>Coppie certe: <b className="font-mono text-emerald-700">{analisi.coppie_certe}</b> · Ambigui: <b className="font-mono text-amber-700">{analisi.ambigui}</b> · Senza corrispondenza: <b className="font-mono">{analisi.senza_corrispondenza}</b></p>
            <p className="text-slate-400">Eseguita il {(analisi.eseguita_il || "").replace("T", " ").slice(0, 16)} — la scrittura degli SKU su Shopify NON è fatta da qui e richiede il permesso di scrittura prodotti sulle app.</p>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="non-merce-block">
        <h2 className="text-sm font-semibold text-slate-800 mb-1">Righe non-merce ({(data.righe_non_merce_riconosciute || []).reduce((s, r) => s + r.occorrenze, 0)} riconosciute)</h2>
        <p className="text-xs text-slate-400 mb-3">
          Righe che non sono merce (es. "Commissione Contrassegno" da 3 €): restano nei ricavi ma non contano come pezzi venduti e non hanno costo.
          Il riconoscimento è per SKU esatto o per testo contenuto nel nome riga (senza distinzione maiuscole/minuscole) e vale per tutti gli store.
        </p>
        {regole && (
          <div className="flex flex-wrap gap-2 mb-3" data-testid="non-merce-regole">
            {regole.globali.map((r, i) => (
              <span key={i} className="text-xs bg-slate-100 border border-slate-200 rounded-full px-3 py-1 font-mono flex items-center gap-2">
                {r.tipo === "sku" ? "SKU =" : "nome contiene"} "{r.valore}"
                <button data-testid={`non-merce-rm-${i}`} onClick={() => salvaRegole(regole.globali.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600 font-bold">×</button>
              </span>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <select data-testid="non-merce-tipo" className="border border-slate-300 rounded px-2 py-1.5 text-sm" value={nuovaRegola.tipo} onChange={(e) => setNuovaRegola({ ...nuovaRegola, tipo: e.target.value })}>
            <option value="nome">nome contiene</option>
            <option value="sku">SKU esatto</option>
          </select>
          <input data-testid="non-merce-valore" className="border border-slate-300 rounded px-2 py-1.5 text-sm flex-1 min-w-[220px]" placeholder="es. commissione contrassegno" value={nuovaRegola.valore} onChange={(e) => setNuovaRegola({ ...nuovaRegola, valore: e.target.value })} />
          <button data-testid="non-merce-add" onClick={() => aggiungiRegola()} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded px-3 py-1.5">Aggiungi regola</button>
        </div>
        {(data.righe_non_merce_riconosciute || []).length > 0 && (
          <p data-testid="non-merce-riconosciute" className="text-xs text-slate-500 mb-3">
            Riconosciute finora: {data.righe_non_merce_riconosciute.map((r) => `${r.nome} (${r.occorrenze}×)`).join(" · ")}
          </p>
        )}
        <div data-testid="non-merce-suggerimenti">
          <h3 className="text-xs font-semibold text-amber-700 mb-1">Possibili righe non-merce da classificare (ricorrenti, senza SKU né costo)</h3>
          {(data.suggerimenti_non_merce || []).length === 0 ? (
            <p className="text-xs text-slate-400">Nessun suggerimento: tutte le righe ricorrenti sono già classificate.</p>
          ) : (
            data.suggerimenti_non_merce.slice(0, 10).map((s) => (
              <div key={s.nome} className="flex items-center gap-3 py-1 border-b border-slate-50 text-xs">
                <span className="font-mono flex-1">{s.nome} <span className="text-slate-400">— {s.occorrenze} volte, es. ordine {s.esempio_ordine}</span></span>
                {s.nome !== "(senza nome)" && (
                  <button data-testid={`non-merce-classifica-${s.nome}`} onClick={() => aggiungiRegola(s.nome, "nome")} className="text-indigo-600 font-semibold hover:underline">Classifica come non-merce</button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="costo-incompatibili-block">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h2 className="text-sm font-semibold text-slate-800">Prezzo non compatibile con la regola di ricostruzione ({(data.costo_incompatibili || []).length})</h2>
          <a data-testid="export-costi-incompatibili" href={`${API}/api/dati-mancanti/export/costo_incompatibili?store_id=${storeId}`} target="_blank" rel="noreferrer"
            className="text-xs font-semibold text-indigo-600 border border-indigo-200 rounded px-3 py-1.5 hover:bg-indigo-50">Scarica CSV</a>
        </div>
        <p className="text-xs text-slate-400 mb-2">
          Prodotti del master dove costo = prezzo (tool di importazione) ma prezzo meno il ricarico della collezione darebbe zero o meno:
          la regola NON è stata applicata e il costo reale resta sconosciuto. Come si risolve: correggi prezzo o costo sul master, oppure adegua la regola in Configurazione.
        </p>
        {(data.costo_incompatibili || []).length === 0 ? (
          <p className="text-sm text-emerald-700">Nessun prodotto incompatibile: la regola si applica ovunque serva.</p>
        ) : (
          <table className="w-full text-sm" data-testid="costo-incompatibili-table">
            <thead><tr className="text-left border-b border-slate-200">
              <th className="py-1.5 text-xs font-bold uppercase text-slate-600">SKU</th>
              <th className="text-xs font-bold uppercase text-slate-600">Prodotto</th>
              <th className="text-xs font-bold uppercase text-slate-600 text-right">Prezzo</th>
              <th className="text-xs font-bold uppercase text-slate-600 text-right">Detrazione tentata</th>
            </tr></thead>
            <tbody>
              {data.costo_incompatibili.slice(0, 100).map((p, i) => (
                <tr key={i} className="border-b border-slate-50">
                  <td className="py-1.5 font-mono text-xs">{p.sku}</td>
                  <td className="text-xs">{p.nome}</td>
                  <td className="font-mono text-xs text-right">{p.prezzo != null ? fmtEUR(p.prezzo) : "—"}</td>
                  <td className="font-mono text-xs text-right text-red-700">−{fmtEUR(p.detrazione)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="text-sm font-semibold text-slate-800 mb-3">Metodi di pagamento da mappare ({data.metodi_da_mappare.length})</h2>
        {data.metodi_da_mappare.length === 0 ? <p className="text-sm text-slate-400">Tutti i metodi sono mappati.</p> : (
          <table className="w-full text-sm" data-testid="metodi-da-mappare-table">
            <thead><tr className="text-left border-b border-slate-200">
              <th className="py-1.5 text-xs font-bold uppercase text-slate-600">Valore grezzo</th>
              <th className="text-xs font-bold uppercase text-slate-600">Famiglia</th>
              <th className="text-xs font-bold uppercase text-slate-600">Dettaglio</th>
              <th />
            </tr></thead>
            <tbody>
              {data.metodi_da_mappare.map((raw) => (
                <tr key={raw} className="border-b border-slate-50">
                  <td className="py-1.5 font-mono text-xs">{raw}</td>
                  <td>
                    <select data-testid={`map-famiglia-${raw}`} className="border border-slate-300 rounded px-2 py-1 text-sm"
                      onChange={(e) => setMap({ ...map, [raw]: { ...map[raw], famiglia: e.target.value } })}>
                      <option value="">—</option>
                      {FAMIGLIE.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </td>
                  <td>
                    <select data-testid={`map-dettaglio-${raw}`} className="border border-slate-300 rounded px-2 py-1 text-sm"
                      onChange={(e) => setMap({ ...map, [raw]: { ...map[raw], dettaglio: e.target.value } })}>
                      <option value="">—</option>
                      {DETTAGLI.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </td>
                  <td className="text-right">
                    {isMaster && (
                      <button data-testid={`map-save-${raw}`} onClick={() => salvaMapping(raw)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded px-3 py-1">Salva mappatura</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!isMaster && data.metodi_da_mappare.length > 0 && (
          <p className="text-xs text-slate-400 mt-2">La mappatura dei gateway è riservata al master.</p>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="catalogo-block">
        <button data-testid="catalogo-toggle" onClick={() => setShowCatalogo((v) => !v)}
          className="w-full flex items-center justify-between gap-3 text-left">
          <span>
            <span className="font-heading text-lg font-bold tracking-tight text-slate-900">Catalogo da sistemare, senza impatto sugli ordini</span>
            <span className="block text-xs text-slate-400 mt-0.5">
              Prodotti del catalogo senza SKU o senza costo ({data.prodotti_mancanti.length}): in gran parte articoli vecchi mai venduti.
              Nessun effetto sui numeri del periodo — pulizia generale per quando hai tempo.
            </span>
          </span>
          <span className="text-xs font-semibold text-indigo-600 shrink-0">{showCatalogo ? "Chiudi ▲" : "Apri ▼"}</span>
        </button>
        {showCatalogo && (
          <div className="mt-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-xs text-slate-400">Come si risolve: compila SKU e "Costo per articolo" su Shopify — la prossima sincronizzazione aggiorna tutto. Il CSV è l'elenco completo per lavorare offline.</p>
              <a data-testid="export-catalogo" href={`${API}/api/dati-mancanti/export/catalogo?store_id=${storeId}`} target="_blank" rel="noreferrer"
                className="text-xs font-semibold text-indigo-600 border border-indigo-200 rounded px-3 py-1.5 hover:bg-indigo-50 shrink-0">Scarica CSV</a>
            </div>
            {data.prodotti_mancanti.length === 0 ? <p className="text-sm text-slate-400">Nessun prodotto incompleto.</p> : (
              <table className="w-full text-sm" data-testid="prodotti-mancanti-table">
                <thead><tr className="text-left border-b border-slate-200">
                  <th className="py-1.5 text-xs font-bold uppercase text-slate-600">Prodotto</th>
                  <th className="text-xs font-bold uppercase text-slate-600">SKU</th>
                  <th className="text-xs font-bold uppercase text-slate-600 text-right">Costo</th>
                  {isMaster && <th className="text-xs font-bold uppercase text-slate-600">Store</th>}
                </tr></thead>
                <tbody>
                  {data.prodotti_mancanti.slice(0, 200).map((p) => (
                    <tr key={p.id} className="border-b border-slate-50">
                      <td className="py-1.5">{p.nome}</td>
                      <td className="font-mono text-xs">{p.sku || <span className="text-amber-600">mancante</span>}</td>
                      <td className="font-mono text-xs text-right">{p.costo != null ? fmtEUR(p.costo) : <span className="text-amber-600">mancante</span>}</td>
                      {isMaster && <td className="text-xs text-slate-500">{p.store_nome}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {data.prodotti_mancanti.length > 200 && (
              <p className="text-xs text-slate-400 mt-2">Mostrate le prime 200 di {data.prodotti_mancanti.length}: l'elenco completo è nel CSV.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
